import React, { useState, useCallback } from 'react';
import { Stack, Text, Button, Overlay } from '@moda/om';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { usePaymentInfoEnteredTracking } from '../../../hooks/useCheckout';
import { reportError } from '../../../lib/reportError';
import { LoadingBalls } from '../../LoadingBalls';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { AmazonShippingAndPaymentWidgets } from '../AmazonShippingAndPaymentWidgets';
import { useAmazonBillingAgreementMutation } from './useAmazonBillingAgreementMutation';
import './AmazonShippingAndPaymentForm.scss';

type Props = {
  onClose: () => void;
  onCancel: () => void;
  onSubmit?: () => Promise<void>;
};

export const AmazonShippingAndPaymentForm: React.FC<Props> = ({ onClose, onCancel, onSubmit }) => {
  const { showErrorModal } = useErrorModal();
  const [billingAgreementId, setBillingAgreementId] = useState('');
  const [consentStatus, setConsentStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createBillingAgreement] = useAmazonBillingAgreementMutation(billingAgreementId);
  const { trackPaymentInfoEntered } = usePaymentInfoEnteredTracking();

  const handleWidgetsReady = useCallback(
    (billingAgreementId: string) => {
      setBillingAgreementId(billingAgreementId);
    },
    [setBillingAgreementId]
  );

  const handleConsentStatusChange = useCallback(
    (checked: boolean) => {
      setConsentStatus(checked);
    },
    [setConsentStatus]
  );

  const handleSubmit = useCallback(async () => {
    if (!billingAgreementId) return;

    setLoading(true);

    try {
      await createBillingAgreement();
      await onSubmit?.();
      trackPaymentInfoEntered();
      onClose();
    } catch (error) {
      reportError(error);
      showErrorModal();
    }

    setLoading(false);
  }, [
    billingAgreementId,
    onClose,
    onSubmit,
    showErrorModal,
    createBillingAgreement,
    trackPaymentInfoEntered
  ]);

  return (
    <>
      <Stack space={4}>
        <Stack space={1}>
          <Text treatment="h5">Edit Amazon Information</Text>
          <Text>
            Amazon requires you to confirm both your address and payment method when editing.
          </Text>
        </Stack>
        <AmazonShippingAndPaymentWidgets
          onReady={handleWidgetsReady}
          onConsentStatusChange={handleConsentStatusChange}
        />
        <FormButtonsLayout>
          <Button secondary onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!billingAgreementId || consentStatus === false} onClick={handleSubmit}>
            Continue
          </Button>
        </FormButtonsLayout>
      </Stack>

      <Overlay className="AmazonShippingAndPaymentForm__overlay" show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
